import { Select, SelectProps } from "@shopify/polaris"
import { Option } from "polaris-enhance"
import { useCallback, useEffect, useMemo } from "react"

import { RuleItem } from "../../utils"
import { useValidate } from "../global"

export type ProSelectProps<T extends string | number> = Omit<
  SelectProps,
  "options" | "value" | "onChange"
> & {
  /**
   * 校验rules，用法类似antd
   */
  rules?: RuleItem[]
  /**
   * 选择改变事件
   */
  onChange?(value: T): void
  /**
   * 是否必填
   */
  required?: boolean
  value?: T
  options: Option<T>[]
  /**
   * 当只有一个值时，是否自动选择
   */
  selectedWhenOnlyOneValue?: boolean
  /**
   * 当只有一个选项时禁用选项
   */
  disabledWhenOnlyOneOption?: boolean
}

function hasUnmatchedValue(options: Option<any>[], value: any) {
  return (
    options.length > 0 && value && options.every((item) => item.value != value)
  )
}

const ProSelect = <T extends string | number>(props: ProSelectProps<T>) => {
  const { error, handleFocus, handleBlur, id } = useValidate({
    id: props.id,
    error: props.error,
    value: hasUnmatchedValue(props.options, props.value) ? "" : props.value,
    onlyRequired: props.required,
  })

  useEffect(() => {
    if (props.selectedWhenOnlyOneValue && props.options.length === 1) {
      props.onChange?.(props.options[0].value as T)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options.length, props.selectedWhenOnlyOneValue])

  const handleChange = useCallback(
    (val: string | number) => {
      const newValue =
        typeof props.value === "number" ? Number(val) : val.toString()
      props.onChange?.(newValue as T)
    },
    [props],
  )

  // 格式化后的options
  const options = useMemo(() => {
    const options =
      hasUnmatchedValue(props.options, props.value) || !props.value
        ? [
            {
              label: t("PleaseSelected"),
              value: "",
              disabled: false,
            },
            ...(props.options ?? []),
          ]
        : (props.options ?? [])
    return options.map((item) => {
      return {
        label: item.label.toString(),
        value: item.value.toString(),
        disabled: item.disabled,
      }
    })
  }, [props.value, props.options])

  return (
    <Select
      {...props}
      options={options}
      value={props.value?.toString()}
      id={id}
      error={error}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      disabled={
        props.disabled
          ? props.disabled
          : props.disabledWhenOnlyOneOption &&
            props.options.length === 1 &&
            props.options[0]?.value === props.value
      }
    />
  )
}

export default ProSelect
